import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { isEqual } from 'lodash';
import { ChangeEvent, memo, ReactElement, useState } from 'react';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useMemoCompare from '~/hooks/useMemoCompare';
import { IAllocationClientsByGroup } from '~/services/api/clientAllocation/types';
import { IClientOptionByTeam } from '~/store/client/types';
import useRole from '~/store/user/hooks/useRole';
import { ReactComponent as GlobeIcon } from '~/ui/assets/images/global.svg';
import smallAvatar from '~/ui/assets/images/smallAvatar.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import PrivateImage from '~/ui/components/common/PrivateImage';
import ErrorTooltip from '~/ui/pages/ActTeam/reusable/ErrorTooltip';
import { ITeamMemberMapped } from '~/utils/clientAllocation';
import { AllocationMode } from '../../constants/allocationMode';
import useSelectedClientsData from '../../hooks/useSelectedClientsData';
import AccordionMembersDetailsFields from './components/AccordionMembersDetails/AccordionMembersDetailsFields';

import styles from './AccordionMembers.module.scss';

interface IProps {
  member: ITeamMemberMapped;
  datesValidation?: { isValid: boolean; users: { userId: number; unavailableDates: string[] }[] };
  clientsList: IClientOptionByTeam[];
  hasGlobalUserRole: boolean;
  clientAllocationDate: string;
  errorDates: string[];
  showDifferentTeamErrorMessage: boolean;
  renderSelect: () => ReactElement;
  allocationMode: AllocationMode;
  locationGroups: IAllocationClientsByGroup[];
  handleRemoveClient: (id: number, field: string) => void;
  handleRemoveClientInLocationGroup: (id: number, field: string, groupName?: string) => void;
  formFieldName: string;
  validateAvailability?: boolean;
}

const AccordionMembers = ({
  member,
  clientsList,
  renderSelect,
  hasGlobalUserRole,
  clientAllocationDate,
  errorDates,
  showDifferentTeamErrorMessage,
  allocationMode,
  locationGroups,
  handleRemoveClient,
  handleRemoveClientInLocationGroup,
  formFieldName,
  datesValidation,
  validateAvailability,
}: IProps): ReactElement => {
  const [expandedMemberId, setExpandedMemberId] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (_event: ChangeEvent<Record<string, unknown>>, isExpanded: boolean) => {
      setExpandedMemberId(isExpanded ? panel : false);
    };

  const { getClientsByLocationGroup, getSelectedClients, getSelectedFieldsByName } =
    useSelectedClientsData(formFieldName);
  const { isGlobalUser } = useRole();

  const watchedClients = getSelectedFieldsByName('clients', formFieldName) as number[];
  const watchedGroups = getSelectedFieldsByName('groups', formFieldName) as number[];
  const selectedClients = getSelectedClients(clientsList);
  const selectedClientsByLocationGroup = getClientsByLocationGroup(locationGroups, watchedGroups);

  const filteredClients = useMemoCompare(
    selectedClients.filter(sClient => (watchedClients || []).includes(sClient.id)),
    (prev, next) => prev && isEqual(prev, next),
  );

  if (member.isDeleted && !clientAllocationDate) {
    return null;
  }

  return (
    <>
      <Accordion
        className={styles.accordion}
        TransitionProps={{ unmountOnExit: true }}
        expanded={expandedMemberId === String(member.id)}
        onChange={handleChange(String(member.id))}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Grid container>
            <Grid item xs={4} className={styles.accordionSummaryItemName}>
              <div className={styles.photoContainer}>
                <PrivateImage
                  className={styles.image}
                  src={member.photo || smallAvatar}
                  alt="avatar"
                />
                {hasGlobalUserRole && (
                  <div className={styles.absoluteIcon}>
                    <GlobeIcon width={16} height={16} color={variables.colorGreenGlobal} />
                  </div>
                )}
              </div>
              <Typography>{member.fullName}</Typography>
              {!datesValidation?.isValid && !!errorDates?.length && (
                <ErrorTooltip items={errorDates} color={variables.colorOrange} />
              )}
            </Grid>

            <Grid item xs={4} className={styles.accordionSummaryItemCenter}>
              <Typography>{watchedClients?.length || 0} Allocated Clients</Typography>
            </Grid>

            <Grid item xs={4} className={styles.accordionSummaryItemRight}>
              <Typography>Details</Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        {showDifferentTeamErrorMessage && (
          <Grid item sm={12} className={styles.validationErrors}>
            Note: this user has assigned client allocations for this date on other teams
          </Grid>
        )}

        <AccordionMembersDetailsFields
          validateAvailability={validateAvailability}
          allocationMode={allocationMode}
          selectedClientsByLocationGroup={selectedClientsByLocationGroup}
          selectedClients={filteredClients}
          renderSelect={renderSelect}
          formFieldName={formFieldName}
          handleRemoveClient={handleRemoveClient}
          handleRemoveClientInLocationGroup={handleRemoveClientInLocationGroup}
          watchedClients={watchedClients}
          isGlobalUser={isGlobalUser}
          memberId={member.id}
        />
      </Accordion>
    </>
  );
};

export default memo(AccordionMembers);
